import { useCallback, useEffect, useState } from "react";
import { useRouteLoaderData } from "react-router";
import type { loader as rootLoader } from "~/root";

/**
 * Theme picker values. `system` defers to `prefers-color-scheme` and reacts
 * to OS-level dark-mode switches while it is selected.
 */
export type Theme = "light" | "dark" | "system";

type RootLoaderData = Awaited<ReturnType<typeof rootLoader>>;

/** One year. Long enough that the user's choice survives across visits. */
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const THEME_REGEX = /(?:^|;\s*)theme=(light|dark|system)/;

export function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

/**
 * Pure cookie-string parser. Both the client (`readThemeCookie`) and the
 * server loader (`readThemeFromRequest` in app/root.tsx) go through this so
 * a regression in one path can't drift from the other.
 */
export function parseThemeFromCookieString(cookieString: string): Theme {
  const match = cookieString.match(THEME_REGEX);
  const value = match?.[1];
  return isTheme(value) ? value : "system";
}

/**
 * Read the theme cookie from `document.cookie`. SSR-safe: returns "system"
 * when `document` is not defined (the inline theme script in root.tsx is the
 * source of truth for the first paint — by the time this hook runs client
 * side, the cookie has already been parsed by that script).
 */
export function readThemeCookie(): Theme {
  if (typeof document === "undefined") return "system";
  return parseThemeFromCookieString(document.cookie);
}

export function writeThemeCookie(theme: Theme): void {
  if (typeof document === "undefined") return;
  // `SameSite=Lax` is enough: the cookie is read on top-level navigation
  // and on the same-site fetch, both of which Lax covers. No `Secure` flag
  // so http://localhost:8788 dev still works.
  document.cookie = `theme=${theme}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
}

/** Returns the OS-level theme preference, defaulting to dark if unknown. */
export function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * Map a Theme to the actual class that should be applied to <html>. The
 * `systemPreference` argument is optional — when omitted, `getSystemTheme()`
 * is used. Tests pass an explicit value to avoid the DOM dependency.
 */
export function resolveTheme(
  theme: Theme,
  systemPreference?: "light" | "dark",
): "light" | "dark" {
  return theme === "system" ? (systemPreference ?? getSystemTheme()) : theme;
}

/** Toggle the `dark` class on <html> based on the resolved theme. */
export function applyResolvedTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveTheme(theme) === "dark");
}

export interface ThemeState {
  /** The user's stored preference (light | dark | system). */
  theme: Theme;
  /** Update the cookie + DOM class + local state in one step. */
  setTheme: (next: Theme) => void;
}

/**
 * Reactive hook for the active theme. Initial state comes from the root
 * loader so SSR and the first client render agree (no hydration mismatch
 * for the toggle dropdown). On the client, an effect also syncs from the
 * cookie in case the loader data drifted from the source of truth.
 */
export function useTheme(): ThemeState {
  const data = useRouteLoaderData("root") as RootLoaderData | undefined;
  const [theme, setThemeState] = useState<Theme>(
    () => data?.initialTheme ?? "system",
  );

  // Keep <html class="dark"> in sync with the current resolved theme.
  // The inline script in root.tsx already set the class for the first
  // paint; this effect re-applies it whenever the user picks a new value
  // via the toggle.
  useEffect(() => {
    applyResolvedTheme(theme);
  }, [theme]);

  // When theme === 'system', react to OS-level switches.
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyResolvedTheme("system");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    writeThemeCookie(next);
    setThemeState(next);
  }, []);

  return { theme, setTheme };
}
